/**
 * IntegrationSyncStatusBadge
 *
 * Subtle indicator for integration sync error state. Surfaces the rate-limit /
 * auth-expired / temporary error state coming back from the backend on
 * integration row payloads (e.g. `GET /integrations/google/accounts`).
 *
 * Field contract (matches the backend agent's IntegrationSyncError shape):
 *   - syncErrorCode: 'RATE_LIMITED' | 'AUTH_EXPIRED' | 'SCOPE_INSUFFICIENT' | 'TEMPORARY' | 'UNKNOWN' | null
 *   - syncError:     short user-facing message (string | null)
 *   - syncErrorAt:   ISO timestamp when the error was last set (string | null)
 *
 * When `syncErrorCode === null`, this renders nothing — the surrounding UI
 * keeps showing whatever it shows today (e.g. "synced N min ago").
 *
 * Visual design choices:
 *   - RATE_LIMITED        → amber (expected & recoverable, sync auto-resumes)
 *   - TEMPORARY           → amber (transient backoff, also auto-recovers)
 *   - EXPIRED_CURSOR      → amber (sync token expired, full resync auto-triggered)
 *   - AUTH_EXPIRED        → red   (user must reconnect)
 *   - SCOPE_INSUFFICIENT  → red   (user must grant additional permissions)
 *   - CONFLICT            → red   (per-item write conflict, needs attention)
 *   - UNKNOWN             → red   (treat as actionable until we know better)
 *
 * Two visual variants:
 *   - 'inline' (default): tiny icon + short text suitable for an account row
 *   - 'banner': sticky pill / banner suitable for a panel header where the
 *     active account is rate-limited and the user is staring at empty content
 */
import { ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export type IntegrationSyncErrorCode =
  | 'RATE_LIMITED'
  | 'AUTH_EXPIRED'
  | 'SCOPE_INSUFFICIENT'
  | 'TEMPORARY'
  | 'UNKNOWN'
  /** 412 stale ETag — per-item write conflict, needs attention (two-way sync §7). */
  | 'CONFLICT'
  /** 410 expired syncToken — full resync auto-triggered (two-way sync §9). */
  | 'EXPIRED_CURSOR';

export interface IntegrationSyncErrorFields {
  syncErrorCode?: IntegrationSyncErrorCode | null;
  syncError?: string | null;
  syncErrorAt?: string | null;
}

interface IntegrationSyncStatusBadgeProps extends IntegrationSyncErrorFields {
  variant?: 'inline' | 'banner';
  /** Optional override for the message — defaults to syncError, then a stable fallback. */
  fallbackMessage?: string;
}

/**
 * Defense-in-depth fallback copy used only when the backend sets `syncErrorCode`
 * but omits `syncError`. The backend should always set both — these fallbacks
 * exist so the UI never renders a bare badge with no message.
 *
 * Contract: keep these strings aligned with the backend's standard copy
 * patterns (see API sync error setters in apps/api/src/services/*-sync-service.ts).
 * If you change one, change the other.
 */
const FALLBACK_MESSAGES: Record<IntegrationSyncErrorCode, string> = {
  RATE_LIMITED: 'Rate limited, retrying soon',
  AUTH_EXPIRED: 'Reconnect required',
  SCOPE_INSUFFICIENT: 'Additional permissions required',
  TEMPORARY: 'Sync paused, retrying soon',
  UNKNOWN: 'Sync paused',
  CONFLICT: 'Sync conflict — needs attention',
  EXPIRED_CURSOR: 'Resyncing…',
};

/** Severity buckets — amber for expected/recoverable, red for action-required. */
function severityFor(code: IntegrationSyncErrorCode): 'warning' | 'danger' {
  if (code === 'RATE_LIMITED' || code === 'TEMPORARY' || code === 'EXPIRED_CURSOR') return 'warning';
  return 'danger';
}

/** Pick the icon — clock for rate-limit/temporary/expired-cursor, alert triangle for action-required. */
function IconFor({ code, size }: { code: IntegrationSyncErrorCode; size: number }) {
  if (code === 'RATE_LIMITED' || code === 'TEMPORARY' || code === 'EXPIRED_CURSOR') {
    return <ClockIcon width={size} height={size} aria-hidden="true" />;
  }
  return <ExclamationTriangleIcon width={size} height={size} aria-hidden="true" />;
}

const COLORS = {
  warning: { bg: '#FEF3C7', text: '#92400E', icon: '#B45309' },
  danger:  { bg: '#FEE2E2', text: '#991B1B', icon: '#B91C1C' },
} as const;

export function IntegrationSyncStatusBadge({
  syncErrorCode,
  syncError,
  variant = 'inline',
  fallbackMessage,
}: IntegrationSyncStatusBadgeProps) {
  if (!syncErrorCode) return null;

  const severity = severityFor(syncErrorCode);
  const palette = COLORS[severity];
  const message = syncError || fallbackMessage || FALLBACK_MESSAGES[syncErrorCode];

  if (variant === 'banner') {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: palette.bg,
          color: palette.text,
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.35,
        }}
      >
        <span style={{ color: palette.icon, display: 'inline-flex', flexShrink: 0 }}>
          <IconFor code={syncErrorCode} size={16} />
        </span>
        <span>{message}</span>
      </div>
    );
  }

  // inline variant
  return (
    <span
      role="status"
      aria-live="polite"
      title={message}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: palette.text,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
      }}
    >
      <span style={{ color: palette.icon, display: 'inline-flex', flexShrink: 0 }}>
        <IconFor code={syncErrorCode} size={12} />
      </span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {message}
      </span>
    </span>
  );
}
